import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchMyStore } from '../store/storeSlice';
import axiosInstance from '../../axiosConfig';
import './BrandCommunity.css';

// Icons
const UsersIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
  </svg>
);

const MessageIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
  </svg>
);

const CrownIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M2 17l4-4 4 4 4-4 4 4V7l-4 4-4-4-4 4-4-4z" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
  </svg>
);

const ToggleIcon = ({ enabled }) => (
  <div className={`bc-toggle ${enabled ? 'bc-toggle-on' : ''}`}>
    <div className="bc-toggle-handle" />
  </div>
);

const BrandCommunity = () => {
  const dispatch = useDispatch();
  const store = useSelector(s => s.store.current);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [community, setCommunity] = useState(null);
  const [stats, setStats] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isActive: true
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');

  const communityUrl = process.env.REACT_APP_COMMUNITY_URL || 'https://community.userevstar.com';

  useEffect(() => {
    // Ensure store is loaded
    if (!store) {
      dispatch(fetchMyStore());
    }
  }, [dispatch, store]);

  useEffect(() => {
    fetchBrandCommunity();
  }, []);

  const fetchBrandCommunity = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/merchant/brand-community');
      const data = response.data;

      if (data.communities && data.communities.length > 0) {
        const myComm = data.communities[0];
        setCommunity(myComm);
        setFormData({
          name: myComm.name || '',
          description: myComm.description || '',
          isActive: myComm.isActive !== false
        });
        setStats(myComm.tierStats || {});
      }
    } catch (err) {
      console.error('Failed to fetch brand community:', err);
    } finally {
      setLoading(false);
    }
  };

  const createCommunity = async () => {
    try {
      setSaving(true);
      setError('');

      const response = await axiosInstance.post('/api/merchant/brand-community', {
        storeId: store?.id,
        name: formData.name || `${store?.name} VIP Club`,
        description: formData.description
      });

      if (response.data.success) {
        setCommunity(response.data.community);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Failed to create brand community:', err);
      setError(err.response?.data?.message || 'Failed to create community');
    } finally {
      setSaving(false);
    }
  };

  const updateCommunity = async () => {
    if (!community) return;

    try {
      setSaving(true);
      setError('');

      const response = await axiosInstance.put(`/api/merchant/brand-community/${community.id}`, {
        name: formData.name,
        description: formData.description,
        isActive: formData.isActive
      });

      if (response.data.success) {
        setCommunity(response.data.community);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Failed to update brand community:', err);
      setError(err.response?.data?.message || 'Failed to update community');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (community) {
      updateCommunity();
    } else {
      createCommunity();
    }
  };

  const getTierCount = (tier) => stats?.[tier] || 0;
  const totalMembers = Object.values(stats || {}).reduce((sum, count) => sum + count, 0);

  if (loading) {
    return (
      <div className="bc-page">
        <div className="bc-loading">
          <div className="bc-spinner" />
          <p>Loading brand community...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bc-page">
      <div className="bc-header">
        <div className="bc-header-content">
          <h1>Brand Community</h1>
          <p>Create a VIP community for your loyal customers</p>
        </div>
        {community && (
          <a
            href={`${communityUrl}/brand/${store?.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bc-btn bc-btn-outline"
          >
            <ExternalLinkIcon />
            View Community
          </a>
        )}
      </div>

      {showSuccess && (
        <div className="bc-alert bc-alert-success">
          {community ? 'Community settings saved!' : 'Community created successfully!'}
        </div>
      )}

      {error && (
        <div className="bc-alert bc-alert-error">
          {error}
        </div>
      )}

      {/* Stats Cards */}
      {community && (
        <div className="bc-stats-grid">
          <div className="bc-stat-card">
            <div className="bc-stat-icon">
              <UsersIcon />
            </div>
            <div className="bc-stat-content">
              <span className="bc-stat-value">{totalMembers}</span>
              <span className="bc-stat-label">Total Members</span>
            </div>
          </div>
          <div className="bc-stat-card">
            <div className="bc-stat-icon bc-stat-bronze">
              <CrownIcon />
            </div>
            <div className="bc-stat-content">
              <span className="bc-stat-value">{getTierCount('BRONZE')}</span>
              <span className="bc-stat-label">Bronze</span>
            </div>
          </div>
          <div className="bc-stat-card">
            <div className="bc-stat-icon bc-stat-silver">
              <CrownIcon />
            </div>
            <div className="bc-stat-content">
              <span className="bc-stat-value">{getTierCount('SILVER')}</span>
              <span className="bc-stat-label">Silver</span>
            </div>
          </div>
          <div className="bc-stat-card">
            <div className="bc-stat-icon bc-stat-gold">
              <CrownIcon />
            </div>
            <div className="bc-stat-content">
              <span className="bc-stat-value">{getTierCount('GOLD')}</span>
              <span className="bc-stat-label">Gold</span>
            </div>
          </div>
          <div className="bc-stat-card">
            <div className="bc-stat-icon bc-stat-platinum">
              <CrownIcon />
            </div>
            <div className="bc-stat-content">
              <span className="bc-stat-value">{getTierCount('PLATINUM')}</span>
              <span className="bc-stat-label">Platinum</span>
            </div>
          </div>
          <div className="bc-stat-card">
            <div className="bc-stat-icon">
              <MessageIcon />
            </div>
            <div className="bc-stat-content">
              <span className="bc-stat-value">{community.postCount || 0}</span>
              <span className="bc-stat-label">Posts</span>
            </div>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <div className="bc-card">
        <div className="bc-card-header">
          <h2>{community ? 'Community Settings' : 'Create Your VIP Community'}</h2>
          <p>{community ? 'Manage your brand community settings' : 'Build a community for your most loyal customers'}</p>
        </div>

        <form onSubmit={handleSubmit} className="bc-form">
          {community && (
            <div className="bc-form-group bc-form-toggle">
              <div className="bc-toggle-label">
                <span>Community Active</span>
                <p>When disabled, customers cannot access the community</p>
              </div>
              <button
                type="button"
                className="bc-toggle-btn"
                onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
              >
                <ToggleIcon enabled={formData.isActive} />
              </button>
            </div>
          )}

          <div className="bc-form-group">
            <label htmlFor="name">Community Name</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={`${store?.name || 'Your Store'} VIP Club`}
            />
          </div>

          <div className="bc-form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Welcome to our exclusive VIP community! Get early access to new products, exclusive discounts, and connect with fellow fans."
              rows={4}
            />
          </div>

          <div className="bc-form-actions">
            <button type="submit" className="bc-btn bc-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : community ? 'Save Changes' : 'Create Community'}
            </button>
          </div>
        </form>
      </div>

      {/* How It Works */}
      {!community && (
        <div className="bc-card bc-info-card">
          <h3>How Brand Community Works</h3>
          <div className="bc-info-grid">
            <div className="bc-info-item">
              <div className="bc-info-number">1</div>
              <div className="bc-info-content">
                <h4>Create Your Community</h4>
                <p>Set up your VIP community with a custom name and description</p>
              </div>
            </div>
            <div className="bc-info-item">
              <div className="bc-info-number">2</div>
              <div className="bc-info-content">
                <h4>Customers Join Automatically</h4>
                <p>Your customers can join from your storefront and earn tier status based on their purchases</p>
              </div>
            </div>
            <div className="bc-info-item">
              <div className="bc-info-number">3</div>
              <div className="bc-info-content">
                <h4>Engage & Reward</h4>
                <p>Share exclusive content, run polls, and offer VIP rewards to your community</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tier Info */}
      <div className="bc-card">
        <div className="bc-card-header">
          <h2>Customer Tiers</h2>
          <p>Customers earn points based on their orders and move up tiers</p>
        </div>

        <div className="bc-tiers-grid">
          <div className="bc-tier-card bc-tier-bronze">
            <div className="bc-tier-header">
              <CrownIcon />
              <span>Bronze</span>
            </div>
            <p className="bc-tier-points">0 - 499 points</p>
            <ul className="bc-tier-perks">
              <li>Community access</li>
              <li>Early access announcements</li>
            </ul>
          </div>
          <div className="bc-tier-card bc-tier-silver">
            <div className="bc-tier-header">
              <CrownIcon />
              <span>Silver</span>
            </div>
            <p className="bc-tier-points">500 - 1,999 points</p>
            <ul className="bc-tier-perks">
              <li>All Bronze perks</li>
              <li>10% exclusive discount</li>
            </ul>
          </div>
          <div className="bc-tier-card bc-tier-gold">
            <div className="bc-tier-header">
              <CrownIcon />
              <span>Gold</span>
            </div>
            <p className="bc-tier-points">2,000 - 4,999 points</p>
            <ul className="bc-tier-perks">
              <li>All Silver perks</li>
              <li>Free shipping</li>
            </ul>
          </div>
          <div className="bc-tier-card bc-tier-platinum">
            <div className="bc-tier-header">
              <CrownIcon />
              <span>Platinum</span>
            </div>
            <p className="bc-tier-points">5,000+ points</p>
            <ul className="bc-tier-perks">
              <li>All Gold perks</li>
              <li>VIP events access</li>
            </ul>
          </div>
        </div>

        <p className="bc-tier-note">
          Customers earn 1 point per ₦100 spent + 50 bonus points per order
        </p>
      </div>
    </div>
  );
};

export default BrandCommunity;
